import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api';

// Query Keys
export const stationQueryKeys = {
  all: ['stations'],
  lists: (params) => [...stationQueryKeys.all, 'list', params],
  list: (params) => [...stationQueryKeys.lists(params)],
  details: () => [...stationQueryKeys.all, 'detail'],
  detail: (id) => [...stationQueryKeys.details(), id],
  batch: (ids) => [...stationQueryKeys.all, 'batch', ids],
  status: (ids) => [...stationQueryKeys.all, 'status', ids],
  mutations: () => [...stationQueryKeys.all, 'mutations'],
};

// 충전소 목록 조회
export const useStations = (params = {}) => {
  return useQuery({
    queryKey: stationQueryKeys.list(params),
    queryFn: () => api.get('/drivecore/resource/api/Station', { params }),
    staleTime: 5 * 60 * 1000,
    // 페이지별로 캐시를 구분하기 위한 설정
    keepPreviousData: false,
  });
};

// 충전소 상세 조회
export const useStation = (id) => {
  return useQuery({
    queryKey: stationQueryKeys.detail(id),
    queryFn: async () => {
      const response = await api.get('/drivecore/resource/api/Station', { 
        params: { StationSysId: id } 
      });
      // Items 배열의 첫 번째 요소 반환, 없으면 null
      return response?.Items?.[0] || null;
    },
    enabled: !!id,
  });
};

// 충전소 배치 조회
export const useStationsBatch = (stationIds) => {
  return useQuery({
    queryKey: stationQueryKeys.batch(stationIds),
    queryFn: async () => {
      if (!stationIds || stationIds.length === 0) return [];
      
      const promises = stationIds.map(async (id) => {
        const response = await api.get('/drivecore/resource/api/Station', { 
          params: { StationSysId: id } 
        });
        // Items 배열의 첫 번째 요소 반환, 없으면 null
        return response?.Items?.[0] || null;
      });
      
      const results = await Promise.all(promises);
      // null 값 필터링
      return results.filter(station => station !== null);
    },
    enabled: stationIds && stationIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
};

// 충전소 상태 조회 (대기/사용/불가)
export const useStationStatus = (stationIds) => {
  return useQuery({
    queryKey: stationQueryKeys.status(stationIds),
    queryFn: async () => {
      if (!stationIds || stationIds.length === 0) return {};
      
      try {
        const response = await api.post('/drivecore/stats/api/station/status/batch', {
          stationIds: stationIds
        });
        
        return response.data || {};
      } catch (error) {
        console.error('충전소 상태 API 호출 실패:', error);
        // API 호출 실패시 기본값 반환
        const defaultStatus = {};
        stationIds.forEach(id => {
          defaultStatus[id] = {
            WaitingCount: 2,
            InUseCount: 10,
            UnavailableCount: 3
          };
        });
        return defaultStatus;
      }
    },
    enabled: stationIds && stationIds.length > 0,
    staleTime: 1 * 60 * 1000, // 1분간 fresh 상태 유지 (실시간 데이터)
    cacheTime: 5 * 60 * 1000, // 5분간 메모리에 보관
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// 충전소 생성
export const useCreateStation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (stationData) => api.post('/drivecore/resource/api/Station', stationData),
    onSuccess: () => {
      // 충전소 목록 쿼리 무효화
      queryClient.invalidateQueries({ queryKey: stationQueryKeys.all });
    },
  });
};

// 충전소 수정
export const useUpdateStation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ data }) => api.put(`/drivecore/resource/api/Station`, data),
    onSuccess: () => {
      // 충전소 목록 쿼리 무효화
      queryClient.invalidateQueries({ queryKey: stationQueryKeys.all });
    },
  });
};

// 충전소 삭제
export const useDeleteStation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => api.delete(`/drivecore/resource/api/Station/${id}`),
    onSuccess: () => {
      // 충전소 목록 쿼리 무효화
      queryClient.invalidateQueries({ queryKey: stationQueryKeys.all });
    },
  });
};
